import React, { useState, useEffect, useRef } from 'react';

const INTRO_VIDEO_URL = "https://customer-assets.emergentagent.com/job_jamaica-law-firm/artifacts/dxwmiek9_download.mp4";

export const IntroVideo = ({ onComplete }) => {
  const [fadeOut, setFadeOut] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Check if user has already seen the intro (session storage)
    const hasSeenIntro = sessionStorage.getItem('hasSeenIntro');
    if (hasSeenIntro) {
      setIsVisible(false);
      onComplete();
      return;
    }

    // Fallback: Skip intro after 5 seconds if video doesn't load
    timeoutRef.current = setTimeout(() => {
      console.log('Video load timeout, skipping intro');
      handleComplete();
    }, 5000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onComplete]);

  const handleComplete = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setFadeOut(true);
    sessionStorage.setItem('hasSeenIntro', 'true');
    
    // Wait for fade animation to complete
    setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, 1200);
  };

  const handleVideoEnd = () => {
    handleComplete();
  };

  const handleVideoLoaded = () => {
    setVideoLoaded(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    const video = videoRef.current;
    if (video) {
      video.play().catch(err => {
        console.log('Autoplay failed:', err);
      });
    }
  };

  const handleVideoError = () => {
    console.log('Video error, skipping intro');
    handleComplete();
  };

  const handleSkip = () => {
    handleComplete();
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 z-[9999] bg-[#0d0a08] flex items-center justify-center transition-opacity duration-1000 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}
      data-testid="intro-video-container"
    >
      <video
        ref={videoRef}
        src={INTRO_VIDEO_URL}
        className="w-full h-full object-contain max-h-screen"
        onEnded={handleVideoEnd}
        onCanPlay={handleVideoLoaded}
        onError={handleVideoError}
        muted
        playsInline
        autoPlay
        preload="auto"
        data-testid="intro-video"
      />
      
      {/* Skip button - always visible */}
      <button
        onClick={handleSkip}
        className="absolute bottom-10 right-10 px-8 py-3 bg-[#C9A86C]/20 hover:bg-[#C9A86C]/40 text-white text-sm uppercase tracking-[0.2em] font-medium transition-all border border-[#C9A86C]/50 hover:border-[#C9A86C] rounded-sm backdrop-blur-sm"
        data-testid="skip-intro-btn"
      >
        Skip Intro
      </button>
      
      {/* Loading indicator */}
      {!videoLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0d0a08]">
          <div className="text-center">
            <div className="w-12 h-12 border-2 border-[#C9A86C]/30 border-t-[#C9A86C] rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#C9A86C]/70 text-sm tracking-widest uppercase">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroVideo;
